import { useMemo, useState } from 'react'
import { useWelcomeHeader } from '../../hooks/landing/useWelcomeHeader'
import { useAuth } from '../../contexts/AuthContext'
import { useNavigate } from 'react-router-dom'
import AnimatedLogo from './AnimatedLogo'
import { WELCOME_SUBTITLE } from '../../constants/landing/welcome.constants'
import { Power, Bell } from 'lucide-react'
import { useNotifications } from '../../contexts/NotificationContext'
import NotificationModal from '../NotificationModal'
import '../../layouts/WelcomeHeader.css'

export default function WelcomeHeader() {
  const {
    currentMessage,
    headerRef,
    messageRef,
    handleMessageClick,
    displayName
  } = useWelcomeHeader()
  const { isAuthenticated, logout, user } = useAuth()
  const navigate = useNavigate()
  const [isNotificationModalOpen, setIsNotificationModalOpen] = useState(false)
  
  const { unreadCount } = useNotifications()

  // Vérifier si l'utilisateur est un client
  const isClient = isAuthenticated && user?.role?.toLowerCase() === 'client'
  
  // Vérifier si l'utilisateur est un professionnel
  const isProfessional = isAuthenticated && user?.role?.toLowerCase() === 'professional'
  
  // Vérifier si l'utilisateur est un admin
  const isAdmin = isAuthenticated && user?.role?.toLowerCase() === 'admin'
  
  // Pour les professionnels, afficher le nom de la société ou le nom personnel
  const professionalDisplayName = useMemo(() => {
    if (!isProfessional || !user) return null
    
    // Gérer les deux formats possibles (camelCase et snake_case)
    const professionalInfo = user.professionalInfo || user.professional_info
    const companyName = professionalInfo?.companyName || professionalInfo?.company_name
    
    // Priorité : nom de la société > nom complet > prénom
    if (companyName) {
      return companyName
    }
    
    const fullName = [user.firstName, user.lastName]
      .filter(Boolean)
      .join(' ')
      .trim()
    
    return fullName || user.firstName || null
  }, [isProfessional, user])
  
  // Pour les admins, afficher le nom complet
  const adminDisplayName = useMemo(() => {
    if (!isAdmin || !user) return null
    
    // Gérer les deux formats possibles (camelCase et snake_case)
    const firstName = user.firstName || user.first_name
    const lastName = user.lastName || user.last_name
    
    const fullName = [firstName, lastName]
      .filter(Boolean)
      .join(' ')
      .trim()
    
    return fullName || firstName || null
  }, [isAdmin, user])

  const handleLogout = () => {
    logout()
    navigate('/')
  }

  return (
    <header className="welcome-header" ref={headerRef}>
      <div className="welcome-container">
        <div className="welcome-actions">
          {isAuthenticated && (
            <>
              <button
                onClick={() => setIsNotificationModalOpen(true)}
                className="notification-button"
                title="Notifications"
                aria-label="Voir les notifications"
              >
                <Bell className="notification-icon" />
                {unreadCount > 0 && (
                  <span className="notification-badge">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </button>
              <button
                onClick={handleLogout}
                className="logout-button"
                title="Déconnexion"
                aria-label="Se déconnecter"
              >
                <Power className="logout-icon" />
              </button>
            </>
          )}
        </div>
        <div className="welcome-main">
          <div className="welcome-content-with-logo">
            <AnimatedLogo />
            <div className="welcome-text">
              <h1
                className="welcome-message"
                onClick={handleMessageClick}
                title="Cliquez pour changer le message"
              >
                <span className="welcome-greeting" ref={messageRef}>{currentMessage}</span>
                {(isClient || isProfessional || isAdmin) && (
                  <>
                    <span className="welcome-separator">, </span>
                    <span className="welcome-name">
                      {isProfessional 
                        ? professionalDisplayName 
                        : isAdmin 
                        ? adminDisplayName 
                        : displayName}
                    </span>
                  </>
                )}
              </h1>
              <p className="welcome-inspiration">
                {WELCOME_SUBTITLE}
              </p>
            </div>
          </div>
        </div>
      </div>
      {isAuthenticated && (
        <NotificationModal
          isOpen={isNotificationModalOpen}
          onClose={() => setIsNotificationModalOpen(false)}
        />
      )}
    </header>
  )
}

